/**
 * B3 (Kabir 2026-10-11, ruling 2: "yes", store the captured number or email, with consent recorded separately).
 *
 * ONE DEFINITION of the contact shapes. `packages/pipeline/src/redact.ts` scrubs the frozen archive with the email
 * and NANP phone shapes below, and the live capture (packages/db/src/contactIdentities.ts) finds them in a
 * customer's own message with the same shapes. Before this file the shapes lived only in redact.ts.
 *
 * The detector is deterministic code, never a model (CLAUDE.md section 4: math and parsing go through code). It
 * is STRICTER than the redactor, on purpose, and in the opposite direction: a redactor that over-matches costs a
 * scrubbed digit run, a detector that over-matches stores a stranger's order number as a customer's phone. So
 * every shape match is then checked, and anything ambiguous is not a capture (CLAUDE.md section 0, B13):
 *   - a phone must have separators in the NANP shape (a bare ten-digit run could be an order number), pass the
 *     NANP area and exchange rules (first digit 2-9, not N11), and not sit in a
 *     word, a price, a path or a longer digit run, or right after an order, invoice, job, case or similar label;
 *   - an international number must be written with its `+`, and have 8 to 15 digits (E.164's ceiling);
 *   - an email must have a clean local part and domain labels, and not end in an image or document extension
 *     (`photo@2x.png`).
 *
 * Normalized: a phone to E.164 (`+12125550147`), an email with its domain lower-cased and its local part kept as
 * typed (RFC 5321 leaves the local part's case to the receiving server).
 *
 * The result carries the kind and the normalized value only, never the surrounding text. The value is customer
 * identity (CLAUDE.md section 10): the one caller that stores it encrypts it first, and nothing logs it.
 */

export type ContactKind = "phone" | "email";

export interface ContactHit {
  readonly kind: ContactKind;
  /** Normalized: E.164 for a phone, domain lower-cased for an email. */
  readonly value: string;
}

/** The redactor's email shape, verbatim (redact.ts used it inline until B3). */
export const EMAIL_SHAPE_SOURCE = String.raw`[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}`;

/** The redactor's phone shape, verbatim: NANP's 3-3-4 with an optional `+1` or `+NN` prefix. */
export const NANP_PHONE_SHAPE_SOURCE = String.raw`(?:\+\d{1,2}[\s.-]?)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}\b`;

/**
 * An international number written with its `+` country code, in groups. The redactor does not use this shape (its
 * output on the frozen archive is unchanged by B3); its digit-run rule catches the long unseparated runs.
 */
const INTERNATIONAL_PHONE_SHAPE_SOURCE = String.raw`\+\d{1,3}(?:[\s.-]?\(?\d{1,6}\)?){1,6}`;

/**
 * Q1 TA-1204 review fix round 1: the shape the detector and the model-input mask find emails with. It finds what
 * EMAIL_SHAPE_SOURCE plus validEmail accept, but is linear: the local part starts only where a local-part run starts
 * and is at most 64 chars (validEmail refuses a longer one), and every domain label is at most 63. The redactor keeps
 * EMAIL_SHAPE_SOURCE verbatim, so its output on the frozen archive is unchanged.
 */
const EMAIL_FIND_SHAPE_SOURCE = String.raw`(?<![A-Za-z0-9._%+-])[A-Za-z0-9._%+-]{1,64}@(?:[A-Za-z0-9-]{1,63}\.){1,127}[A-Za-z]{2,24}`;

/** A fresh global RegExp each call: a shared global RegExp carries `lastIndex` between callers. */
export function emailShape(): RegExp {
  return new RegExp(EMAIL_SHAPE_SOURCE, "g");
}

/** A fresh global RegExp each call, for the same reason. */
export function nanpPhoneShape(): RegExp {
  return new RegExp(NANP_PHONE_SHAPE_SOURCE, "g");
}

/**
 * The labels a reference number is written after. A phone-shaped number after one of these is an order or job
 * number until a person says otherwise.
 */
const REFERENCE_LABEL =
  /(?:#|\b(?:order|ord|po|p\.o|job|invoice|inv|ref|reference|case|ticket|tracking|track|item|sku|receipt|confirmation|conf|acct|account|card|serial|style|model|cert|certificate|gia|igi|lot|stock|claim|rma|repair|appraisal)\b\.?)\s*(?:no\.?|num\.?|number|#)?\s*[:#]?\s*$/i;

/**
 * C3: a reference label ANYWHERE earlier in the same short clause ("my order number is 212-555-0147"). The anchored
 * REFERENCE_LABEL above only sees a label directly before the number. A clause ends at a comma, semicolon, `!`,
 * `?`, a line break or a full stop followed by a space. Precision first (section 0): "in case you need me 212-..."
 * is refused too, and costs one follow-up question.
 */
// Q1 TA-1204 review fix round 1: a grading report, certificate or laser inscription number is a reference too ("the
// GIA report is 2185965471"). `lab` only when it is not "lab grown" or "lab created", which says nothing about a
// number, so "is it lab grown call me 2125550147" still reads as a phone.
const CLAUSE_REFERENCE_LABEL =
  /\b(?:order|ord|po|p\.o|job|invoice|inv|ref|reference|case|tracking|reports?|certs?|certificates?|certification|gia|igi|ags|hrd|lab(?![\s-]*(?:grown|created|made))|inscription|inscribed|laser)\b/i;
const CLAUSE_BREAK = /[,;!?\n]|\.\s/;

/**
 * Q1 TA-1204 review fix round 1: a list of long numbers after one label ("GIA: 2185965471, 2185965472"). The label
 * carries across the commas and "and"s to every number in the list. Only digit runs of six or more chars count as
 * list items, so "order 5, 2125550147" still reads the phone as a phone.
 */
const NUMBER_LIST_TAIL = /(?:\d[\d.-]{4,}\d\s*(?:,|&|\/|\band\b)\s*)+$/i;

/** C2: the text to the left ends with a `+` country code, optionally followed by a `(0)` trunk group. */
const COUNTRY_CODE_LEFT = /\+\d{1,3}[\s.-]?(?:\(0\)?[\s.-]?)?$/;

/** File and image extensions that make `name@2x.png` look like an address. Shared with contactMask.ts (one list, F-16 spirit). Read-only to importers (L4, TA-1275). */
export const FILE_TLDS: ReadonlySet<string> = new Set(["png", "jpg", "jpeg", "gif", "webp", "heic", "heif", "bmp", "svg", "tif", "tiff", "pdf", "mp4", "mov", "avi", "zip", "doc", "docx", "xls", "xlsx", "csv", "txt"]);

const DOMAIN_LABEL = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/;

/**
 * Q1 TA-1204: a hit with where it sits, `start` inclusive and `end` exclusive, for the model-input mask
 * (contactMask.ts). A NANP number written with its trunk `1-` in front spans the trunk too, so the mask leaves no
 * stray `1-` behind. Every hit, repeats included, in text order.
 */
export interface ContactSpan extends ContactHit {
  readonly start: number;
  readonly end: number;
}

const NANP_TRUNK_LEFT = /(?:^|[^A-Za-z0-9])1[\s.-]$/;

export function findContactSpans(text: string): ContactSpan[] {
  const found: ContactSpan[] = [];
  // Emails first, then masked, so the digits of `2125550147@carrier.example` are never read again as a phone.
  let masked = text;
  for (const m of text.includes("@") ? text.matchAll(new RegExp(EMAIL_FIND_SHAPE_SOURCE, "g")) : []) {
    const at = m.index ?? 0;
    const value = validEmail(text, m[0], at);
    masked = masked.slice(0, at) + " ".repeat(m[0].length) + masked.slice(at + m[0].length);
    if (value !== null) found.push({ kind: "email", value, start: at, end: at + m[0].length });
  }
  for (const m of masked.matchAll(nanpPhoneShape())) {
    const at = m.index ?? 0;
    const value = validNanpShape(masked, m[0], at);
    if (value === null) continue;
    const trunk = !m[0].startsWith("+") && NANP_TRUNK_LEFT.test(masked.slice(Math.max(0, at - 3), at));
    found.push({ kind: "phone", value, start: trunk ? at - 2 : at, end: at + m[0].length });
  }
  for (const m of masked.matchAll(new RegExp(INTERNATIONAL_PHONE_SHAPE_SOURCE, "g"))) {
    const at = m.index ?? 0;
    const value = validInternational(masked, m[0], at);
    if (value !== null) found.push({ kind: "phone", value, start: at, end: at + m[0].length });
  }
  return found.sort((a, b) => a.start - b.start);
}

export function detectContactDetails(text: string): ContactHit[] {
  const seen = new Set<string>();
  const out: ContactHit[] = [];
  for (const h of findContactSpans(text)) {
    const key = `${h.kind}:${h.value}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ kind: h.kind, value: h.value });
  }
  return out;
}

/**
 * Q1 TA-1204, for the model-input mask's wider phone shapes only (contactMask.ts): true when the text around a
 * candidate number makes it a reference, a price, a path, part of a word or part of a longer run. The same left and
 * right rules the capture applies, so a number the capture reads as an order number is never masked as a phone.
 */
export function phoneContextRefused(text: string, start: number, end: number): boolean {
  return badLeft(text, start, { allowTrunk: false }) || badRight(text, end);
}

/** Q1 TA-1204: ten national digits that pass the NANP area and exchange rules (the capture's own check). */
export function isPlausibleNanp(national: string): boolean {
  return validNanp(national);
}

function validEmail(text: string, match: string, at: number): string | null {
  const after = text.charAt(at + match.length);
  if (/[A-Za-z0-9_@-]/.test(after)) return null;
  const sep = match.lastIndexOf("@");
  const local = match.slice(0, sep);
  const domain = match.slice(sep + 1);
  if (local.length === 0 || local.length > 64 || match.length > 254) return null;
  if (local.startsWith(".") || local.endsWith(".") || local.includes("..")) return null;
  const labels = domain.split(".");
  if (labels.length < 2 || !labels.every((l) => DOMAIN_LABEL.test(l))) return null;
  const tld = labels[labels.length - 1]!.toLowerCase();
  if (!/^[a-z]{2,24}$/.test(tld) || FILE_TLDS.has(tld)) return null;
  return `${local}@${domain.toLowerCase()}`;
}

/** True when what precedes `at` makes the number a reference, a price, a path or part of a word. */
function badLeft(text: string, at: number, opts: { readonly allowTrunk: boolean }): boolean {
  let left = text.slice(Math.max(0, at - 40), at);
  // A NANP trunk prefix written before the number ("1-212-555-0147") is part of the number, not a word.
  if (opts.allowTrunk) {
    const trunk = /(?:^|[^A-Za-z0-9])1[\s.-]$/.exec(left);
    if (trunk) left = left.slice(0, left.length - 2);
  }
  const prev = left.charAt(left.length - 1);
  if (/[A-Za-z0-9_$#/@&*+=%.,-]/.test(prev)) return true;
  if (labelled(left)) return true;
  const listed = left.replace(NUMBER_LIST_TAIL, "");
  return listed !== left && labelled(listed);
}

function labelled(left: string): boolean {
  if (REFERENCE_LABEL.test(left)) return true;
  const clause = left.split(CLAUSE_BREAK).pop() ?? "";
  return CLAUSE_REFERENCE_LABEL.test(clause);
}

/** True when what follows the match continues it: more digits, a word, an address, or a dotted or dashed tail. */
function badRight(text: string, end: number): boolean {
  const next = text.slice(end, end + 2);
  if (/^[A-Za-z0-9_@]/.test(next)) return true;
  return /^[./-]\d/.test(next);
}

const isNxx = (d: string): boolean => /^[2-9]\d\d$/.test(d) && !(d[1] === "1" && d[2] === "1");

/**
 * `national` is ten digits. The NANP rules that are cheap to check: area code and exchange each start 2-9 and are
 * not N11. The 555-01XX block is reserved as fictional and is NOT refused: no real person holds one, so capturing
 * one costs nothing, and the tests use it so that no test carries a number a real person might hold.
 */
function validNanp(national: string): boolean {
  if (!/^\d{10}$/.test(national)) return false;
  return isNxx(national.slice(0, 3)) && isNxx(national.slice(3, 6));
}

/** E.164 digits after the `+`: 8 to 15, no leading 0, and a country code 1 must be a valid NANP number. */
function validE164Digits(digits: string): string | null {
  if (!/^[1-9]\d{7,14}$/.test(digits)) return null;
  if (digits.startsWith("1")) return digits.length === 11 && validNanp(digits.slice(1)) ? `+${digits}` : null;
  return `+${digits}`;
}

function validNanpShape(text: string, match: string, at: number): string | null {
  const plus = match.startsWith("+");
  if (badLeft(text, at, { allowTrunk: !plus })) return null;
  // C2: the tail of an international number ("+44 (0) 207 946 0958") is NANP-shaped but is not a US number. The
  // international matcher owns every number written with a + country code.
  if (COUNTRY_CODE_LEFT.test(text.slice(Math.max(0, at - 12), at))) return null;
  if (badRight(text, at + match.length)) return null;
  // Parentheses, when used, wrap the area code and only the area code.
  const open = match.indexOf("(");
  const close = match.indexOf(")");
  if ((open === -1) !== (close === -1)) return null;
  const digits = match.replace(/\D/g, "");
  if (!plus) return validNanp(digits) ? `+1${digits}` : null;
  return validE164Digits(digits);
}

function validInternational(text: string, match: string, at: number): string | null {
  if (badLeft(text, at, { allowTrunk: false })) return null;
  if (badRight(text, at + match.length)) return null;
  // C2: a `(0)` trunk group directly after the country code is not part of the number (E.164 has no trunk zero):
  // drop it. A `(0)` anywhere else is ambiguous, so the whole capture is refused.
  const lead = /^(\+\d{1,3})([\s.-]?)\(0\)/.exec(match);
  const body = lead ? `${lead[1]}${lead[2]}${match.slice(lead[0].length)}` : match;
  if (/\(\s*0\s*\)/.test(body)) return null;
  if (lead && /[()]/.test(body)) return null;
  const open = (body.match(/\(/g) ?? []).length;
  const close = (body.match(/\)/g) ?? []).length;
  if (open !== close || open > 1) return null;
  return validE164Digits(body.replace(/\D/g, ""));
}
